import React, { FC, useEffect } from 'react';
import { SEO } from 'components/seo/SEO';
import { breadcrumbJsonLd } from 'components/seo/organizationSchema';
import routeManifest from 'routes/routeManifest.json';
import './PrivacyPolicy.css';

const route = routeManifest.find((r) => r.path === '/privacy-policy')!;
const breadcrumbs = breadcrumbJsonLd([
  { name: 'Home', path: '/' },
  { name: 'Privacy Policy', path: '/privacy-policy' },
]);

export const PrivacyPolicy: FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="privacy-policy-page">
      <SEO title={route.title} description={route.description} path={route.path} jsonLd={[breadcrumbs]} />
      <div className="privacy-policy-container">
        <h1>Toastie Privacy Policy</h1>
        <p className="effective-date">Effective Date: 19 Nov 2024</p>

        <section>
          <p>
            Toastie ("we," "our," "us") is committed to protecting your privacy and handling your data responsibly. This Privacy Policy explains how we collect, use, store, and disclose your information when you use the Toastie app ("Services"), including how we use artificial intelligence (AI) to power certain features.
          </p>
          <p>By using Toastie, you agree to this Privacy Policy.</p>
        </section>

        <section>
          <h2>1. Information We Collect</h2>
          <p>We collect personal data and health-related information necessary for the app's core functionality. This may include:</p>
          <ul>
            <li><strong>Health Data:</strong> Information such as symptoms, medications, stool records, menstrual cycle data, and other health-related metrics that you voluntarily input into the app.</li>
            <li><strong>Meal & Food Data:</strong> Photos and descriptions of meals and food items you log, which may be used to identify ingredients via AI.</li>
            <li><strong>Voice & Audio Input:</strong> If you use the speech-to-text feature, audio is temporarily processed to convert your spoken input into text. We do not retain raw audio recordings.</li>
            <li><strong>Lab Reports & Medical Documents:</strong> Documents such as blood test results or other lab reports that you choose to upload for AI-assisted summarisation.</li>
            <li><strong>Account Information:</strong> Your name and email address collected when you create an account.</li>
            <li><strong>Device Information:</strong> Operating system, device identifiers, and related technical data to maintain app performance and compatibility.</li>
          </ul>
        </section>

        <section>
          <h2>2. How We Use Your Data</h2>
          <p>We use your data to provide personalised health tracking services and improve the app's functionality. This includes:</p>
          <ul>
            <li>Tracking and storing your health data to help you monitor your condition over time.</li>
            <li>Sending notifications or reminders related to your health tracking.</li>
            <li>Analysing aggregated, de-identified data to improve app features and performance.</li>
            <li>Generating insights, summaries, and pattern analyses from your tracked data.</li>
            <li>Powering the AI features described in Section 3 below.</li>
          </ul>
        </section>

        <section>
          <h2>3. How We Use Artificial Intelligence</h2>
          <p>Toastie uses AI to power several features. This section explains each use in detail.</p>
          
          <div className="subsection">
            <h3>3.1 Meal Photo Analysis (Ingredient Identification)</h3>
            <p>When you upload a photo of a meal, the image is passed to an AI model to identify likely ingredients. The image is processed solely for this purpose and is not used to train AI models or retained beyond what is necessary for the feature to function.</p>
          </div>

          <div className="subsection">
            <h3>3.2 Speech-to-Text Categorisation</h3>
            <p>When you use voice input, your spoken words are converted to text and then processed by AI to categorise your entry into relevant health categories (e.g. symptoms, medication, meals, stool, menstrual cycle). Audio data is processed transiently and is not stored as a raw recording.</p>
          </div>

          <div className="subsection">
            <h3>3.3 Free-Text Categorisation</h3>
            <p>When you type free-form notes or descriptions, AI is used to interpret and categorise your input into the appropriate health tracking categories. This helps streamline your logging experience without requiring you to manually tag every entry.</p>
          </div>

          <div className="subsection">
            <h3>3.4 AI Health Triage Assistant</h3>
            <p>Toastie includes a conversational AI feature that allows you to ask questions about your health. This assistant may help you determine whether to monitor a symptom, seek medical attention, or take other general actions. <strong>Important: This feature is not a substitute for professional medical advice, diagnosis, or treatment. Always consult a qualified healthcare provider for medical decisions.</strong></p>
          </div>

          <div className="subsection">
            <h3>3.5 Personalised Health Insights (Data-in-Context)</h3>
            <p>With your consent, your tracked health data (including symptoms, medications, meals, and other logged categories) is de-identified to the best of our ability and passed as context to an AI model when you ask it questions about patterns in your health. This allows the AI to provide more relevant and personalised responses. De-identified data means we remove or obscure direct identifiers such as your name and contact details before processing.</p>
          </div>

          <div className="subsection">
            <h3>3.6 Lab Report Upload & Summarisation</h3>
            <p>You may choose to upload lab reports (e.g. blood test results). These documents are processed by AI to produce a plain-language summary and highlight any notable findings. Uploaded documents are de-identified to the best of our ability before AI processing. <strong>Please note: AI-generated summaries of lab reports are for informational purposes only and do not constitute medical advice. Always discuss your results with your healthcare provider.</strong></p>
          </div>

          <div className="ai-principles">
            <h3>AI Data Handling Principles</h3>
            <p>Across all AI features, we apply the following principles:</p>
            <ul>
              <li>We do not use your personal health data to train third-party AI models.</li>
              <li>Data passed to AI models is de-identified to the best of our abilities before processing.</li>
              <li>AI processing is used solely to deliver the feature functionality described above.</li>
              <li>We work only with AI providers whose data processing agreements meet applicable privacy standards.</li>
            </ul>
          </div>
        </section>

        <section>
          <h2>4. Data Storage and Access</h2>
          <p>We store your data securely in our databases. Your health data is only accessible by you. You cannot access or view other users' data.</p>
          <ul>
            <li><strong>Your Data, Your Control:</strong> You have full control over your data. You can view, edit, and delete your information within the app at any time.</li>
            <li><strong>No Third-Party Sale:</strong> We do not sell, trade, or share your personal or health data with third parties for marketing or commercial purposes.</li>
            <li><strong>Secure Storage:</strong> Your data is stored securely using encryption technologies to prevent unauthorised access.</li>
          </ul>
        </section>

        <section>
          <h2>5. Data Security</h2>
          <p>
            We take the security of your personal information seriously and implement a variety of security measures to protect your data, including secure and encrypted storage. While we make every effort to protect your data, please be aware that no method of electronic transmission or storage is 100% secure.
          </p>
        </section>

        <section>
          <h2>6. Data Sharing and Third Parties</h2>
          <p>
            Toastie does not share, sell, or rent your personal or health data to third parties for marketing or commercial purposes. The circumstances under which we may share your data are:
          </p>
          <ul>
            <li><strong>AI Processing Partners:</strong> To deliver AI-powered features, de-identified data may be processed by trusted third-party AI providers under strict data processing agreements. These providers are prohibited from using your data for any purpose other than providing the service.</li>
            <li><strong>Legal Requirements:</strong> If required by law, we may disclose your data to comply with legal obligations, protect our rights, or prevent fraud.</li>
          </ul>
        </section>

        <section>
          <h2>7. Your Rights and Choices</h2>
          <p>As a user of Toastie, you have the following rights:</p>
          <ul>
            <li><strong>Access:</strong> You can access, update, or delete your personal information and health data within the app at any time.</li>
            <li><strong>Deletion:</strong> You can request to have your account and all associated data deleted from our system. Please note that this action is irreversible.</li>
            <li><strong>Opt-Out of AI Features:</strong> Where technically feasible, you may choose not to use AI-powered features. Please note that opting out may limit certain app functionality.</li>
          </ul>
        </section>

        <section>
          <h2>9. Changes to This Privacy Policy</h2>
          <p>
            We may update our Privacy Policy from time to time. Any changes will be communicated through the app, and the updated policy will be posted on this page with a new effective date. We encourage you to review this policy periodically.
          </p>
        </section>

        <section>
          <h2>10. Contact Us</h2>
          <p>
            If you have any questions or concerns about this Privacy Policy or our data practices, please contact us at <a href="mailto:hello@toastie.au">hello@toastie.au</a>.
          </p>
        </section>

        <p className="consent-footer">By using the Toastie app, you consent to this Privacy Policy.</p>
      </div>
    </div>
  );
};
